import {
  EventJsonldEventRead,
  EventJsonldEventWrite,
  EventUserJsonldEventUserRead,
  EventUserJsonldEventUserWrite,
} from '@/api/generated';
import { VARIANT_COLOR_ENUM } from '@/types/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EVENT_USERS_PER_PAGE, EVENTS_PER_PAGE } from 'constants/itemsPerPage';
import { useToaster } from 'context/Toaster/ToasterContext';
import { formatISO9075 } from 'date-fns';
import useApi from 'hooks/useApi';
import { useRouter } from 'next/router';
import { ParsedQuery } from 'query-string';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDistanceUnit } from 'utils/distances';
import {
  EventFilter,
  EventLocatorFilter,
  eventsFactory,
  eventsTypesFactory,
  eventTypeInfosFactory,
  eventUsersFactory,
  MyEventFilter,
} from 'utils/factories/events';

import { useDecodeFilters } from 'utils/filters/encodeDecodeFilters';
import getULID from 'utils/getULID';
import useUserApi from 'hooks/UserApi/useUserApi';

export const useEvents = (params: EventFilter, enabled = true) => {
  const queryKey = eventsFactory.list(params);
  const apiClient = useApi();

  return {
    queryResult: useQuery({
      queryKey,
      queryFn: async () => apiClient.events.apiEventsGetCollection(params),
      select: (res) => res.data,
      enabled,
    }),
    queryKey,
  };
};

export const useShopEventTypes = (enabled = true) => {
  const queryKey = eventsTypesFactory.shop();
  const apiClient = useApi();

  return {
    queryResult: useQuery({
      queryKey,
      queryFn: async () => apiClient.shopEventTypes.apiShopEventTypesGetCollection(),
      select: (res) => res.data,
      enabled,
    }),
    queryKey,
  };
};

type PutEventProps = {
  event: EventJsonldEventWrite;
  id: string;
};

export const usePutEvent = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const { addToaster } = useToaster();
  const { t } = useTranslation();

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: eventsFactory.all() });
    addToaster({ message: t('adminEvent.edition.success'), variant: VARIANT_COLOR_ENUM.SUCCESS });
    if (onSuccess) {
      onSuccess();
    }
  };

  const putEvent = useMutation({
    mutationKey: ['update-event'],
    mutationFn: ({ event, id }: PutEventProps) => apiClient.events.apiEventsIdPut(id, event),
    onSuccess: handleSuccess,
  });

  return { putEvent };
};

type EventDetailProps = {
  eventId: string;
  enabled?: boolean;
};

export const useEventDetailsApi = ({ eventId, enabled = true }: EventDetailProps) => {
  const apiClient = useApi();
  const queryKey = eventsFactory.details(eventId);

  return {
    queryKey,
    queryResult: useQuery({
      queryKey,
      enabled,
      queryFn: async () => apiClient.events.apiEventsIdGet(eventId),
      select: (res) => res.data,
    }),
  };
};

type MutateProps = {
  eventId: string;
  status: EventUserJsonldEventUserWrite['status'];
  deckId?: string;
  userId: string;
  successMessage?: string;
};

type PutProps = {
  event: EventJsonldEventRead;
  status?: EventUserJsonldEventUserWrite['status'];
  deckId?: string;
};

type DeleteProps = {
  event: EventJsonldEventRead;
};

export const useEventUser = () => {
  const apiClient = useApi();
  const queryClient = useQueryClient();
  const { addToaster } = useToaster();
  const { t } = useTranslation();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: eventsFactory.all() });
  };

  const postEventUser = useMutation({
    mutationKey: ['subscribe-event-user'],
    mutationFn: ({ eventId, status, deckId, userId }: MutateProps) =>
      apiClient.eventUsers.apiEventUsersPost({
        event: eventId || '',
        user: userId,
        status,
        ...(deckId ? { deckUserLists: [deckId] } : {}),
      }),
    onSuccess: (_, { successMessage }) => {
      onSuccess();
      addToaster({
        message: t(successMessage || 'event.subscribe.success'),
        variant: VARIANT_COLOR_ENUM.SUCCESS,
        icon: 'fa-solid fa-calendar',
      });
    },
  });

  const putEventUser = useMutation({
    mutationKey: ['update-event-user'],
    mutationFn: ({ event, status, deckId }: PutProps) =>
      apiClient.eventUsers.apiEventUsersIdPut(event.eventUser?.id || '', {
        ...(status ? { status } : {}),
        ...(deckId ? { deckUserLists: [deckId] } : {}),
      }),
    onSuccess: () => {
      onSuccess();
      addToaster({
        message: t('event.addDeck.success'),
        variant: VARIANT_COLOR_ENUM.SUCCESS,
        icon: 'fa-solid fa-cards-blank',
      });
    },
  });

  const deleteEventUser = useMutation({
    mutationKey: ['update-event'],
    mutationFn: ({ event }: DeleteProps) => apiClient.eventUsers.apiEventUsersIdDelete(event.eventUser?.id || ''),
    onSuccess: () => {
      onSuccess();
      addToaster({
        message: t('event.unsubscribe.success'),
        variant: VARIANT_COLOR_ENUM.SUCCESS,
        icon: 'fa-solid fa-calendar-xmark',
      });
    },
  });

  return { postEventUser, putEventUser, deleteEventUser };
};

export type UseEventUserSearchType = {
  'user.uniqueId': string;
  status: string;
};

export const useEventsUsers = (eventId: string, search?: UseEventUserSearchType) => {
  const { query } = useRouter();
  const apiClient = useApi();

  const queryKey = eventUsersFactory.list({ eventId, ...query, page: (query.page as string) || '1' });

  return useQuery({
    queryKey,

    queryFn: () =>
      apiClient.eventUsers.apiEventUsersGetCollection({
        event: eventId,
        itemsPerPage: EVENT_USERS_PER_PAGE,
        page: Number(query.page as string) || 1,
        ...search,
      }),
    select: (res) => res.data,
  });
};

type UserEventPutData = {
  eventUserId: string;
  status: 'PRE_REGISTERED' | 'VALIDATED' | 'ABSENT' | 'ABANDONED';
};

export const useEventAdminUser = () => {
  const { asPath, query } = useRouter();
  const eventId = getULID(asPath) || '';

  const apiClient = useApi();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: eventUsersFactory.all() });
  };

  const eventUserListQueryKey = eventUsersFactory.list({ eventId, ...query, page: (query.page as string) || '1' });

  const changeEventUserStatus = useMutation({
    mutationKey: ['update-event'],
    mutationFn: ({ eventUserId, status }: UserEventPutData) =>
      apiClient.eventUsers.apiEventUsersIdPut(eventUserId || '', {
        status,
      }),
    onSuccess,
    onMutate: async ({ eventUserId, status }) => {
      // Optimistically update the event user status
      queryClient.setQueryData(
        eventUserListQueryKey,
        (oldData: { data: { ['hydra:member']: EventUserJsonldEventUserRead[] } }) => {
          const updatedData = oldData?.data['hydra:member']?.map((eventUser) =>
            eventUser.id === eventUserId ? { ...eventUser, status } : eventUser,
          );

          return {
            ...oldData,
            data: {
              'hydra:member': updatedData,
            },
          };
        },
      );
    },
  });

  return { changeEventUserStatus };
};

const formatDotDateToBracket = (filterValues: ParsedQuery<string>): EventLocatorFilter =>
  Object.keys(filterValues).reduce(
    (acc, cur) =>
      cur.toLowerCase().includes('date')
        ? { ...acc, [cur.replace('.', '[').concat(']')]: filterValues[cur] }
        : { ...acc, [cur]: filterValues[cur] },
    {},
  );

export const useEventLocatorApi = () => {
  const { countryCode, latitude, longitude, displayMap, ...currFilters } = useDecodeFilters();
  const apiClient = useApi();
  const {
    i18n: { language },
  } = useTranslation();

  const now = useMemo(() => formatISO9075(new Date(), { representation: 'date' }), []);

  const formattedFilters: EventLocatorFilter = formatDotDateToBracket(currFilters);
  const startDateTimeAfter =
    formattedFilters?.['startDateTime[after]'] && `${formattedFilters['startDateTime[after]']}T00:00`;

  const startDateTimeBefore =
    formattedFilters?.['startDateTime[before]'] && `${formattedFilters['startDateTime[before]']}T23:59`;

  const defaultDateFilter: EventLocatorFilter = !(startDateTimeAfter || startDateTimeBefore)
    ? {
        'order[startDateTime]': 'asc',
        'endDateTime[after]': now,
      }
    : {};

  const UNIT_DISTANCE = apiDistanceUnit(language);
  const filteredParam: EventLocatorFilter = {
    ...defaultDateFilter,
    ...formattedFilters,
    ...(startDateTimeAfter && {
      'startDateTime[after]': startDateTimeAfter,
    }),
    ...(startDateTimeBefore && {
      'startDateTime[before]': startDateTimeBefore,
    }),
    unit: UNIT_DISTANCE,
    country: (countryCode as string) || undefined,
    ...(currFilters.fullAddress
      ? {
          latitude: Number(latitude) ?? undefined,
          longitude: Number(longitude) ?? undefined,
        }
      : undefined),
    itemsPerPage: EVENTS_PER_PAGE,
  };

  const queryKey = eventsFactory.locator(filteredParam);

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => apiClient.events.apiEventslocatorGetCollection(filteredParam),
    enabled: !!countryCode,
  });

  return { queryResult, queryKey };
};

export const useMyEvents = (params: MyEventFilter, enabled = true) => {
  const queryKey = eventsFactory.myEvents(params);
  const apiClient = useApi();

  return {
    queryResult: useQuery({
      queryKey,
      queryFn: async () => apiClient.myEvents.apiMyEventsGetCollection(params),
      select: (res) => res.data,
      enabled,
    }),
    queryKey,
  };
};

export const useEventTypeInfos = () => {
  const queryKey = eventTypeInfosFactory.list();
  const apiClient = useApi();

  const { user } = useUserApi();

  return useQuery({
    queryKey,
    queryFn: async () => apiClient.eventTypeInformations.apiEventTypeInformationsGetCollection(),
    enabled: !!user,
  });
};
